import React, { useState, useEffect } from 'react';
import { createCheckoutSession, getSubscriptionPlans } from '../../services/subscriptionService';
import { createChildCheckoutSession } from '../../services/parentService';
import { validateReferralCode } from '../../services/epService';
import { toast } from 'react-toastify';
import './SubscriptionModal.css';

interface SubscriptionModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    message?: string;
    childId?: string;
    childName?: string;
}

interface Plan {
    key: string;
    name: string;
    price: number;
    currencySymbol: string;
    period: string;
    priceId: string;
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
    isOpen,
    onClose,
    title,
    message,
    childId,
    childName,
}) => {
    const isParentPurchase = !!childId;
    const modalTitle = title || (isParentPurchase ? `Subscribe for ${childName || 'Your Child'}` : 'Upgrade to Pro');
    const modalMessage = message || (isParentPurchase
        ? `Purchase a subscription plan for ${childName || 'your child'} to unlock all premium features.`
        : 'Subscribe to unlock all premium features and unlimited credits!');
    const [plans, setPlans] = useState<Plan[]>([]);
    const [loading, setLoading] = useState(false);
    const [fetchingPlans, setFetchingPlans] = useState(true);

    // Referral code state
    const [referralCode, setReferralCode] = useState('');
    const [referralStatus, setReferralStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
    const [referralPartner, setReferralPartner] = useState('');
    const [discountPercent, setDiscountPercent] = useState<number | undefined>(undefined);
    const [referralType, setReferralType] = useState<'referral' | 'promo' | undefined>(undefined);
    const normalizedReferralCode = referralCode.trim().toUpperCase();
    const isPromoCode = referralStatus === 'valid' && referralType === 'promo';
    const canApplyPaidPlanDiscount = referralStatus === 'valid' && !isPromoCode;

    useEffect(() => {
        if (isOpen) {
            fetchPlans();
        } else {
            // Reset referral on close
            setReferralCode('');
            setReferralStatus('idle');
            setReferralPartner('');
            setDiscountPercent(undefined);
            setReferralType(undefined);
        }
    }, [isOpen]);

    // Debounced referral code validation
    useEffect(() => {
        if (!referralCode.trim()) {
            setReferralStatus('idle');
            setReferralPartner('');
            setDiscountPercent(undefined);
            setReferralType(undefined);
            return;
        }

        const timer = setTimeout(async () => {
            setReferralStatus('validating');
            try {
                const result = await validateReferralCode(referralCode.trim());
                if (result.valid) {
                    if (isParentPurchase && result.type === 'promo') {
                        setReferralStatus('invalid');
                        setReferralPartner('');
                        setDiscountPercent(undefined);
                        setReferralType(undefined);
                        return;
                    }

                    setReferralStatus('valid');
                    setReferralPartner(result.epName || 'Partner');
                    setDiscountPercent(result.discountPercent);
                    setReferralType(result.type || 'referral');
                } else {
                    setReferralStatus('invalid');
                    setReferralPartner('');
                    setDiscountPercent(undefined);
                    setReferralType(undefined);
                }
            } catch {
                setReferralStatus('invalid');
                setReferralPartner('');
                setDiscountPercent(undefined);
                setReferralType(undefined);
            }
        }, 600);

        return () => clearTimeout(timer);
    }, [referralCode, isParentPurchase]);

    const fetchPlans = async () => {
        try {
            setFetchingPlans(true);
            const backendPlans = await getSubscriptionPlans();

            const planMapping = [
                { key: 'monthly', backendKey: 'MONTHLY', name: 'Monthly Plan', period: 'month' },
                { key: 'halfYear', backendKey: 'SIX_MONTH', name: '6 Month Plan', period: '6 months' },
                { key: 'yearly', backendKey: 'YEARLY', name: 'Yearly Plan', period: 'year' },
            ];

            const formattedPlans = planMapping
                .map(({ key, backendKey, name, period }) => {
                    const backendPlan = backendPlans[backendKey];
                    if (!backendPlan) return null;

                    return {
                        key,
                        name,
                        price: backendPlan.price || 0,
                        currencySymbol: backendPlan.currencySymbol || '$',
                        period,
                        priceId: backendPlan.priceId,
                    };
                })
                .filter((plan): plan is Plan => plan !== null);

            setPlans(formattedPlans);
        } catch (error) {
            console.error('Error fetching plans:', error);
            toast.error('Failed to load subscription plans');
        } finally {
            setFetchingPlans(false);
        }
    };

    const handleSubscribe = async (priceId: string) => {
        if (!priceId || priceId.includes('placeholder')) {
            toast.error('Configuration Error: Price ID not set. Please contact support.');
            return;
        }

        setLoading(true);
        try {
            const validCode = canApplyPaidPlanDiscount ? normalizedReferralCode : undefined;

            let session;
            if (isParentPurchase && childId) {
                session = await createChildCheckoutSession(childId, priceId, validCode);
            } else {
                session = await createCheckoutSession(priceId, validCode);
            }
            if (session && session.url) {
                window.location.href = session.url;
            } else {
                toast.error('Failed to start checkout session');
            }
        } catch (error: any) {
            console.error('Checkout error:', error);
            toast.error(error.response?.data?.message || 'Error initiating checkout');
        } finally {
            setLoading(false);
        }
    };

    const handleActivatePromo = async () => {
        const monthlyPlan = plans.find((plan) => plan.key === 'monthly') || plans[0];
        if (!monthlyPlan?.priceId || monthlyPlan.priceId.includes('placeholder')) {
            toast.error('Configuration Error: Price ID not set. Please contact support.');
            return;
        }

        setLoading(true);
        try {
            const session = await createCheckoutSession(monthlyPlan.priceId, normalizedReferralCode);
            if (session.subscriptionActivated) {
                toast.success(session.message || `${normalizedReferralCode} plan activated for 1 month.`);
                onClose();
                return;
            }

            if (session.url) {
                window.location.href = session.url;
            } else {
                toast.error(`Failed to activate ${normalizedReferralCode} plan.`);
            }
        } catch (error: any) {
            console.error('Promo activation error:', error);
            toast.error(error.response?.data?.message || `Failed to activate ${normalizedReferralCode} plan.`);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="subscription-modal-overlay" onClick={onClose}>
            <div className="subscription-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="subscription-modal-close" onClick={onClose}>
                    ×
                </button>

                <div className="subscription-modal-header">
                    <h2>{modalTitle}</h2>
                    <p>{modalMessage}</p>
                </div>

                {/* ── Referral Code – Professional Flow ── */}
                <div className={`referral-box referral-box--modal${referralStatus === 'valid' ? ' referral-box--applied' : ''}`}>
                    {/* Header */}
                    <div className="referral-box__header">
                        <span className="referral-box__gift-icon">🎁</span>
                        <div className="referral-box__title-group">
                            <p className="referral-box__title">Have a referral code?</p>
                            <p className="referral-box__subtitle">Get an exclusive discount from your partner</p>
                        </div>
                        {referralStatus === 'valid' && (
                            <span className="referral-box__badge">✓ APPLIED</span>
                        )}
                    </div>

                    {/* Input row */}
                    <div className="referral-box__input-row">
                        <div className={`referral-box__input-wrap${referralStatus === 'valid' ? ' is-valid' :
                            referralStatus === 'invalid' ? ' is-invalid' :
                                referralStatus === 'validating' ? ' is-checking' : ''
                            }`}>
                            <span className="referral-box__input-prefix">#</span>
                            <input
                                type="text"
                                className="referral-box__input"
                                placeholder="Enter code  e.g. EP-ABCD1234"
                                value={referralCode}
                                onChange={(e) => setReferralCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
                                maxLength={20}
                                spellCheck={false}
                                autoComplete="off"
                            />
                            {referralStatus === 'validating' && <span className="referral-box__spinner" />}
                            {referralStatus === 'valid' && <span className="referral-box__check">✓</span>}
                            {referralStatus === 'invalid' && referralCode.trim().length > 0 && (
                                <span className="referral-box__cross">✕</span>
                            )}
                        </div>

                        {referralCode.length > 0 && referralStatus !== 'validating' && (
                            <button
                                className="referral-box__clear-btn"
                                onClick={() => { setReferralCode(''); setReferralStatus('idle'); setReferralPartner(''); setDiscountPercent(undefined); setReferralType(undefined); }}
                                title="Clear code"
                            >✕</button>
                        )}
                    </div>

                    {/* Status messages */}
                    {referralStatus === 'validating' && (
                        <p className="referral-box__msg referral-box__msg--checking">
                            <span className="referral-box__dot-pulse" />
                            Verifying your referral code…
                        </p>
                    )}
                    {referralStatus === 'valid' && (
                        <div className="referral-box__success-chip">
                            <span className="referral-box__success-icon">🎉</span>
                            <div>
                                <span className="referral-box__success-title">
                                    {isPromoCode ? `${normalizedReferralCode} Unlocked!` : 'Referral Applied!'} {!isPromoCode && discountPercent ? <strong className="referral-discount-badge">{discountPercent}% OFF</strong> : ''}
                                </span>
                                <span className="referral-box__success-sub">
                                    {isPromoCode
                                        ? 'Separate 1-month promo plan available below'
                                        : <>Referred by <strong>{referralPartner}</strong> · Discount will be applied at checkout</>}
                                </span>
                            </div>
                        </div>
                    )}
                    {referralStatus === 'invalid' && referralCode.trim().length > 0 && (
                        <p className="referral-box__msg referral-box__msg--error">
                            <span>⚠️</span> Invalid code. Please check and try again.
                        </p>
                    )}
                </div>

                {fetchingPlans ? (
                    <div className="subscription-modal-loading">Loading plans...</div>
                ) : (
                    <>
                    {isPromoCode && !isParentPurchase && (
                        <div className="matiyari-promo-card matiyari-promo-card--modal">
                            <div>
                                <span className="matiyari-promo-label">{normalizedReferralCode} Promo Plan</span>
                                <h3>1 Month Free Access</h3>
                                <p className="matiyari-promo-price">₹0 <span>/ month</span></p>
                                <ul>
                                    <li>Included for 1 month</li>
                                    <li>VAD: This feature is not available on this subscription.</li>
                                    <li>Mock Test: 1 mock test per day on this subscription.</li>
                                </ul>
                            </div>
                            <button className="matiyari-promo-button" onClick={handleActivatePromo} disabled={loading}>
                                {loading ? 'Activating...' : `Activate ${normalizedReferralCode} Plan`}
                            </button>
                        </div>
                    )}
                    <div className="subscription-modal-plans">
                        {plans.map((plan) => (
                            <div key={plan.key} className="subscription-plan-card">
                                <h3>{plan.name}</h3>
                                <div className="subscription-plan-price">
                                    <span className="currency">{plan.currencySymbol}</span>
                                    {canApplyPaidPlanDiscount && discountPercent ? (
                                        <>
                                            <span className="amount original-price" style={{ textDecoration: 'line-through', fontSize: '0.5em', opacity: 0.5, marginRight: '8px' }}>
                                                {plan.price}
                                            </span>
                                            <span className="amount">{Math.round(plan.price * (1 - discountPercent / 100))}</span>
                                        </>
                                    ) : (
                                        <span className="amount">{plan.price}</span>
                                    )}
                                    <span className="period">/{plan.period}</span>
                                </div>
                                <ul className="subscription-plan-features">
                                    <li>✓ Unlimited Credits</li>
                                    <li>✓ All Premium Features</li>
                                    <li>✓ Priority Support</li>
                                    <li>✓ Advanced AI Models</li>
                                </ul>
                                <button
                                    className="subscription-plan-button"
                                    onClick={() => handleSubscribe(plan.priceId)}
                                    disabled={loading}
                                >
                                    {loading ? 'Processing...' : 'Subscribe Now'}
                                </button>
                            </div>
                        ))}
                    </div>
                    </>
                )}

                <div className="subscription-modal-footer">
                    <p>Cancel anytime. No hidden fees.</p>
                </div>
            </div>
        </div>
    );
};

export default SubscriptionModal;
